
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";

type ActivityCategory = Tables<"activity_categories">;

interface ActivityFiltersProps {
  categories: ActivityCategory[];
  ageGroups: string[];
  difficultyLevels: string[];
  selectedCategory: string;
  selectedDifficulty: string;
  selectedAgeGroup: string;
  onCategoryChange: (value: string) => void;
  onDifficultyChange: (value: string) => void;
  onAgeGroupChange: (value: string) => void;
  onReset: () => void;
}

export const ActivityFilters = ({
  categories,
  ageGroups,
  difficultyLevels,
  selectedCategory,
  selectedDifficulty,
  selectedAgeGroup,
  onCategoryChange,
  onDifficultyChange,
  onAgeGroupChange,
  onReset,
}: ActivityFiltersProps) => {
  const hasFilters = selectedCategory || selectedDifficulty || selectedAgeGroup;

  const handleCategoryChange = (value: string) => {
    onCategoryChange(value === "all-categories" ? "" : value);
  };

  const handleDifficultyChange = (value: string) => {
    onDifficultyChange(value === "all-levels" ? "" : value);
  };

  const handleAgeGroupChange = (value: string) => {
    onAgeGroupChange(value === "all-ages" ? "" : value);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <div className="min-w-[200px]">
          <Select value={selectedCategory || "all-categories"} onValueChange={handleCategoryChange}>
            <SelectTrigger>
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-categories">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[150px]">
          <Select value={selectedDifficulty || "all-levels"} onValueChange={handleDifficultyChange}>
            <SelectTrigger>
              <SelectValue placeholder="All Levels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-levels">All Levels</SelectItem>
              {difficultyLevels.map((level) => (
                <SelectItem key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[150px]">
          <Select value={selectedAgeGroup || "all-ages"} onValueChange={handleAgeGroupChange}>
            <SelectTrigger>
              <SelectValue placeholder="All Ages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-ages">All Ages</SelectItem>
              {ageGroups.map((ageGroup) => (
                <SelectItem key={ageGroup} value={ageGroup}>
                  {ageGroup}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {hasFilters && (
          <Button variant="outline" onClick={onReset} className="px-3">
            <X className="h-4 w-4 mr-1" />
            Reset
          </Button>
        )}
      </div>

      {hasFilters && (
        <div className="flex flex-wrap gap-2">
          {selectedCategory && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Category: {categories.find(c => c.id === selectedCategory)?.name}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onCategoryChange("")}
              />
            </Badge>
          )}
          {selectedDifficulty && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Difficulty: {selectedDifficulty}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onDifficultyChange("")}
              />
            </Badge>
          )}
          {selectedAgeGroup && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Age: {selectedAgeGroup}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onAgeGroupChange("")}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};
